
import React, { memo, useState, useCallback } from "react";
import { Product } from "@/types/common";
import { Heart, Eye, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

interface OptimizedProductImageProps {
  product: Product;
  isHovered: boolean;
  onQuickView: (e: React.MouseEvent) => void;
  onAddToCart: (e: React.MouseEvent) => void;
}

const OptimizedProductImage = memo<OptimizedProductImageProps>(({ 
  product, 
  isHovered, 
  onQuickView, 
  onAddToCart 
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const isMobile = useIsMobile();

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
    setImageLoaded(true);
  }, []);

  return (
    <div className="relative overflow-hidden aspect-square bg-muted/20">
      {/* Image */}
      {!imageError ? (
        <img
          src={product.image_url || "/placeholder.svg"}
          alt={product.name}
          className={`w-full h-full object-cover transition-all duration-500 ${
            isHovered && !isMobile ? 'scale-110' : 'scale-100'
          } ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          loading="lazy"
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-muted">
          <span className="text-muted-foreground text-sm">No image</span>
        </div>
      )}

      {/* Loading skeleton */}
      {!imageLoaded && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}

      {/* Desktop hover overlay */}
      {!isMobile && (
        <div className={`absolute inset-0 bg-black/60 flex items-center justify-center gap-3 transition-opacity duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}>
          <Button
            size="sm"
            variant="secondary"
            onClick={onQuickView}
            className="gap-2"
          >
            <Eye className="h-4 w-4" />
            Quick View
          </Button>
          <Button
            size="sm"
            onClick={onAddToCart}
            className="gap-2"
          >
            <ShoppingCart className="h-4 w-4" />
            Add to Cart
          </Button>
        </div>
      )}

      {/* Wishlist button */}
      <Button
        size="sm"
        variant="secondary"
        className={`absolute top-3 right-3 h-8 w-8 p-0 transition-all duration-300 ${
          isHovered || isMobile ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
        }`}
      >
        <Heart className="h-4 w-4" />
      </Button>

      {/* Mobile quick add button */}
      {isMobile && (
        <Button
          size="sm"
          onClick={onAddToCart}
          className="absolute bottom-3 right-3 h-8 w-8 p-0"
        >
          <ShoppingCart className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
});

OptimizedProductImage.displayName = 'OptimizedProductImage';

export default OptimizedProductImage;
